/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { QUOTE_TEMPLATES_QUERY } from './graphql/getQuoteTemplates';
import { transformQuoteTemplatesList } from '@/quote-management/data/transforms';
import { NegotiableQuoteTemplatesListModel } from '@/quote-management/data/models/negotiable-quote-template-model';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export enum QuoteTemplateFilterStatus {
  ACTIVE = 'ACTIVE',
  IN_REVIEW = 'IN_REVIEW',
  INACTIVE = 'INACTIVE',
}

export interface QuoteTemplateFilterInput {
  status?: QuoteTemplateFilterStatus[];
  name?: {
    match?: string;
  };
}

export enum QuoteTemplateSortField {
  NAME = 'NAME',
  CREATED_AT = 'CREATED_AT',
  UPDATED_AT = 'UPDATED_AT',
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface QuoteTemplateSortInput {
  sortField: QuoteTemplateSortField;
  sortDirection: SortDirection;
}

export interface GetQuoteTemplatesParams {
  filter?: QuoteTemplateFilterInput;
  pageSize?: number;
  currentPage?: number;
  sort?: QuoteTemplateSortInput;
}

export const getQuoteTemplates = async (
  params: GetQuoteTemplatesParams = {}
): Promise<NegotiableQuoteTemplatesListModel> => {
  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  try {
    const results: any = await fetchGraphQl(QUOTE_TEMPLATES_QUERY, {
      variables: {
        filter: params.filter || null,
        pageSize: params.pageSize || 20,
        currentPage: params.currentPage || 1,
        sort: params.sort || null,
      },
    });

    if (!results?.data?.negotiableQuoteTemplates) {
      throw new Error('No quote templates data received');
    }

    const transformedData = transformQuoteTemplatesList(results.data.negotiableQuoteTemplates);

    if (!transformedData) {
      throw new Error('Failed to transform quote templates data');
    }

    events.emit('quote-management/quote-templates-data', {
      quoteTemplates: transformedData,
      permissions: state.permissions,
    });

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};